import React, { useState } from "react";
import { CloseIcon } from "../../../../../../assets/Icons/CloseIcon";
import styles from "./styles.module.scss";
import { history } from "../../../../../../history";
import { Box } from "../../../../../../shared/Box/Box";
import { getPathParams } from "../../utilities";
import { CLIENTS_ROUTES } from "../../../../../../consts/routes";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { Button } from "../../../../../../shared/Button/Button";
import { ConfirmModal } from "../../../../../../boxes/ConfirmModal/ConfirmModal";
import { DownloadIcon } from "../../../../../../assets/Icons/DownloadIcon";
import { CircularProgress } from "../../../../../../shared/CircularProgress/CircularProgress";
import { useBulkDownload } from "../../hooks/useBulkDownload";
import { useBulkRemoveExpiration } from "../../hooks/useBulkRemoveExpiration";
import { TYPE } from "../../../../../../api/patientDocuments/usePatientDocumentsExpirationDateRemoveMutation";
import { useClientQuery } from "../../../../../../api/queries/useClientQuery";
import { uiNotification } from "../../../../../../services/UINotificationService";

const MAX_BULK_DOWNLOAD_DOCUMENTS = 10;

/**
 * @param {{
 *  selection: {
 *    selected: number[];
 *    resetSelected: () => void;
 *  };
 *  permissions: {
 *    canMutate: boolean;
 *    canView: boolean;
 *  };
 * }} param0
 */
export function Header({ selection, permissions }) {
  var { selected, resetSelected } = selection;
  var { canMutate, canView } = permissions;

  var { patientId } = getPathParams();
  var { tClients: t } = useAppTranslation.Clients();
  var download = useBulkDownload();
  var removeExp = useBulkRemoveExpiration();
  var { data: patient } = useClientQuery({ clientId: patientId });

  var { 0: isRemoveExpDateConfirmOpen, 1: setIsRemoveExpDateConfirmOpen } =
    useState(false);

  var { 0: isRemoveExpWarnConfirmOpen, 1: setIsRemoveExpWarnConfirmOpen } =
    useState(false);

  var handleDownloadClick = () => {
    if (selected.length > MAX_BULK_DOWNLOAD_DOCUMENTS) {
      uiNotification.warn(t("documents.error.document_ids_count"));
      return;
    }

    download.initiate(selected);
  };

  return (
    <Box className={styles.root}>
      <div className={styles.inner}>
        <h3>
          {t("documents.header.title", {
            patientName: patient?.fullName,
            interpolation: { escapeValue: false },
          })}
        </h3>
        <div className={styles.right}>
          {canView && selected.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleDownloadClick}
              isDisabled={download.isLoading}
              leftAdornment={
                download.isLoading ? (
                  <CircularProgress size="small" />
                ) : (
                  <DownloadIcon width="16px" />
                )
              }
            >
              {t("documents.header.download", { count: selected.length })}
            </Button>
          )}
          {canMutate && (
            <Button
              size="small"
              onClick={() => {
                history.push(`/clients/documents/create/${patientId}/profile`);
              }}
            >
              {t("documents.header.add")}
            </Button>
          )}
          <button
            className={styles.close}
            onClick={() => history.push(CLIENTS_ROUTES.profile(patientId))}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className={styles.footer}>
        {canMutate && selected.length > 0 && (
          <Button
            size="tiny"
            variant="text"
            onClick={() => setIsRemoveExpDateConfirmOpen(true)}
          >
            {t("documents.header.remove_exp_date", {
              count: selected.length,
            })}
          </Button>
        )}
        {canMutate && selected.length > 0 && (
          <Button
            size="tiny"
            variant="text"
            onClick={() => setIsRemoveExpWarnConfirmOpen(true)}
          >
            {t("documents.header.remove_exp_warn", {
              count: selected.length,
            })}
          </Button>
        )}
      </div>
      {isRemoveExpDateConfirmOpen && (
        <ConfirmModal
          isOpen
          isConfirming={removeExp.isLoading}
          onClose={() => setIsRemoveExpDateConfirmOpen(false)}
          onCancel={() => setIsRemoveExpDateConfirmOpen(false)}
          onConfirm={() => {
            removeExp.initiate(selected, TYPE.date).then(() => {
              setIsRemoveExpDateConfirmOpen(false);
              resetSelected();
            });
          }}
        >
          {t("documents.header.remove_exp_date_confirm", {
            count: selected.length,
          })}
        </ConfirmModal>
      )}
      {isRemoveExpWarnConfirmOpen && (
        <ConfirmModal
          isOpen
          isConfirming={removeExp.isLoading}
          onClose={() => setIsRemoveExpWarnConfirmOpen(false)}
          onCancel={() => setIsRemoveExpWarnConfirmOpen(false)}
          onConfirm={() => {
            removeExp.initiate(selected, TYPE.acknowledge).then(() => {
              setIsRemoveExpWarnConfirmOpen(false);
              resetSelected();
            });
          }}
        >
          {t("documents.header.remove_exp_warn_confirm", {
            count: selected.length,
          })}
        </ConfirmModal>
      )}
    </Box>
  );
}
